import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { PortalLayout } from '../components/PortalLayout';

/* ── Types ──────────────────────────────────────────────────────────── */

interface UserRecord {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  company_id?: number;
  company_name?: string;
  phone?: string;
  last_login?: string;
  created_at?: string;
  linked_driver_id?: number;
  linked_driver_name?: string;
  allowed_modules?: string[];
}

interface DriverOption {
  id: number;
  name: string;
  card_number?: string;
  email?: string;
}

/* ── All available modules ──────────────────────────────────────────── */

const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard', section: 'Main' },
  { key: 'drivers', label: 'Drivers', section: 'Compliance' },
  { key: 'tacho', label: 'Tacho Analysis', section: 'Compliance' },
  { key: 'calendar', label: 'Worker Calendar', section: 'Compliance' },
  { key: 'compliance', label: 'Compliance & DCRS', section: 'Compliance' },
  { key: 'intelligence', label: 'Intelligence', section: 'Compliance' },
  { key: 'fleet', label: 'Fleet', section: 'Operations' },
  { key: 'tracking', label: 'Vehicle Tracking', section: 'Operations' },
  { key: 'depots', label: 'Depots', section: 'Operations' },
  { key: 'walkaround', label: 'Walkaround Checks', section: 'Operations' },
  { key: 'workshop', label: 'Workshop', section: 'Operations' },
  { key: 'ops', label: 'Operations Board', section: 'Operations' },
  { key: 'quote', label: 'Quotes', section: 'Commercial' },
  { key: 'jobs', label: 'Jobs', section: 'Commercial' },
  { key: 'accounts', label: 'Accounts & Invoicing', section: 'Commercial' },
  { key: 'reports', label: 'Reports', section: 'Administration' },
  { key: 'users', label: 'User Management', section: 'Administration' },
];

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  transport_manager: 'Transport Manager',
  driver: 'Driver',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  suspended: 'bg-red-50 text-red-700',
  pending: 'bg-amber-50 text-amber-700',
};

const EMPTY_INVITE = { email: '', password: '', first_name: '', last_name: '', role: 'driver', phone: '' };

/* ── Helpers ─────────────────────────────────────────────────────────── */

function formatDate(s?: string): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(s?: string): string {
  if (!s) return 'Never';
  return new Date(s).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function timeSince(s?: string): string {
  if (!s) return '';
  const ms = Date.now() - new Date(s).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/* ── Main Component ──────────────────────────────────────────────────── */

export function UsersPage() {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Detail / edit panel
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UserRecord>>({});

  // Create user modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_INVITE);
  const [saving, setSaving] = useState(false);

  // Module permissions editor
  const [editModules, setEditModules] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  // Password reset confirmation
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);

  const isManager = currentUser?.role === 'admin' || currentUser?.role === 'transport_manager';
  const selected = users.find(u => u.id === selectedId) || null;

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [u, d] = await Promise.all([
        api.listUsers(),
        api.getDriversForLinking().catch(() => []),
      ]);
      setUsers(u);
      setDrivers(d);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function showSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  }

  function openDetail(u: UserRecord) {
    setSelectedId(u.id);
    setEditMode(false);
    setEditModules(false);
    setError('');
  }

  function startEdit(u: UserRecord) {
    setEditForm({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      phone: u.phone || '',
      role: u.role,
      linked_driver_id: u.linked_driver_id,
    });
    setEditMode(true);
  }

  function startModuleEdit(u: UserRecord) {
    setSelectedModules(u.allowed_modules || ALL_MODULES.map(m => m.key));
    setEditModules(true);
  }

  async function saveEdit() {
    if (!selectedId) return;
    setSaving(true); setError('');
    try {
      await api.updateUser(selectedId, editForm);
      setEditMode(false);
      loadData();
      showSuccess('User updated');
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function saveModules() {
    if (!selectedId) return;
    setSaving(true); setError('');
    try {
      // If all modules selected, save as null (full access)
      const isAll = ALL_MODULES.every(m => selectedModules.includes(m.key));
      await api.updateUser(selectedId, { allowed_modules: isAll ? null : selectedModules });
      setEditModules(false);
      loadData();
      showSuccess('Module access updated');
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleCreate() {
    if (!createForm.email || !createForm.password || !createForm.first_name || !createForm.last_name) {
      setError('All fields except phone are required'); return;
    }
    setSaving(true); setError('');
    try {
      await api.inviteUser(createForm);
      setShowCreate(false);
      setCreateForm(EMPTY_INVITE);
      loadData();
      showSuccess('User created');
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleResetPassword(u: UserRecord) {
    if (!confirm(`Reset password for ${u.first_name} ${u.last_name}? A new temporary password will be generated and emailed.`)) return;
    setError('');
    try {
      const result = await api.resetUserPassword(u.id);
      setResetResult({ email: u.email, password: result.temp_password });
      showSuccess(`Password reset email sent to ${u.email}`);
    } catch (err: any) { setError(err.message); }
  }

  async function handleSuspend(u: UserRecord) {
    if (!confirm(`Suspend ${u.first_name} ${u.last_name}? They will be unable to log in.`)) return;
    try {
      await api.suspendUser(u.id);
      loadData();
      showSuccess('User suspended');
    } catch (err: any) { setError(err.message); }
  }

  async function handleActivate(u: UserRecord) {
    try {
      await api.activateUser(u.id);
      loadData();
      showSuccess('User activated');
    } catch (err: any) { setError(err.message); }
  }

  async function handleDelete(u: UserRecord) {
    if (!confirm(`Permanently delete ${u.first_name} ${u.last_name} (${u.email})? This cannot be undone.`)) return;
    if (!confirm('Are you absolutely sure? All user data will be removed.')) return;
    try {
      await api.deleteUser(u.id);
      if (selectedId === u.id) setSelectedId(null);
      loadData();
      showSuccess('User deleted');
    } catch (err: any) { setError(err.message); }
  }

  async function handleStatusChange(u: UserRecord, newStatus: string) {
    try {
      await api.updateUser(u.id, { status: newStatus });
      loadData();
      showSuccess(`User ${newStatus === 'active' ? 'approved' : 'status updated'}`);
    } catch (err: any) { setError(err.message); }
  }

  /* ── Filters ─────────────────────────────────────────────────────── */
  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return u.first_name.toLowerCase().includes(s) ||
      u.last_name.toLowerCase().includes(s) ||
      u.email.toLowerCase().includes(s) ||
      (u.company_name || '').toLowerCase().includes(s);
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const activeCount = users.filter(u => u.status === 'active').length;
  const suspendedCount = users.filter(u => u.status === 'suspended').length;

  /* ── User detail panel ──────────────────────────────────────────── */
  if (selected && selectedId) {
    return (
      <PortalLayout currentPage="users">
        <div className="h-[calc(100vh-64px)] overflow-y-auto bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSelectedId(null)} className="text-sm text-gray-500 hover:text-gray-800">← Users</button>
                <div className="h-5 w-px bg-gray-200" />
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
                  {selected.first_name[0]}{selected.last_name[0]}
                </div>
                <div>
                  <div className="text-base font-medium text-gray-900">{selected.first_name} {selected.last_name}</div>
                  <div className="text-xs text-gray-500">{selected.email}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[selected.status] || 'bg-gray-100 text-gray-600'}`}>
                  {selected.status.toUpperCase()}
                </span>
              </div>
              {isManager && selected.id !== currentUser?.id && (
                <div className="flex items-center gap-2">
                  {!editMode && (
                    <button onClick={() => startEdit(selected)} className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">Edit</button>
                  )}
                  <button onClick={() => handleResetPassword(selected)} className="px-3 py-1.5 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50">Reset Password</button>
                  {selected.status === 'active' ? (
                    <button onClick={() => handleSuspend(selected)} className="px-3 py-1.5 text-xs text-amber-600 border border-amber-200 rounded hover:bg-amber-50">Suspend</button>
                  ) : (
                    <button onClick={() => handleActivate(selected)} className="px-3 py-1.5 text-xs text-green-600 border border-green-200 rounded hover:bg-green-50">Activate</button>
                  )}
                  {isAdmin && (
                    <button onClick={() => handleDelete(selected)} className="px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && <div className="mx-6 mt-4 bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
          {success && <div className="mx-6 mt-4 bg-green-50 text-green-700 px-4 py-2 rounded text-sm">{success}</div>}

          {/* Reset password result */}
          {resetResult && (
            <div className="mx-6 mt-4 bg-blue-50 border border-blue-200 rounded p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Password Reset Complete</h4>
                  <p className="text-xs text-blue-700 mt-1">New temporary password for {resetResult.email}:</p>
                  <p className="font-mono text-sm font-bold text-blue-900 mt-1 bg-white px-3 py-1.5 rounded border border-blue-200 inline-block">
                    {resetResult.password}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">This has been emailed to the user. Store it securely.</p>
                </div>
                <button onClick={() => setResetResult(null)} className="text-xs text-blue-500 hover:text-blue-700">Dismiss</button>
              </div>
            </div>
          )}

          <div className="p-6 max-w-4xl space-y-6">

            {/* ── User Details Card ────────────────────────────────── */}
            {editMode ? (
              <div className="bg-white rounded border border-gray-200 p-5">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">Edit User Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">First Name</label>
                    <input type="text" value={editForm.first_name || ''} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                    <input type="text" value={editForm.last_name || ''} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input type="email" value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                    <input type="tel" value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                  </div>
                  {isAdmin && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Role</label>
                      <select value={editForm.role || ''} onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                        <option value="admin">Administrator</option>
                        <option value="transport_manager">Transport Manager</option>
                        <option value="driver">Driver</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Linked Driver (Tacho Card)</label>
                    <select value={editForm.linked_driver_id || 0} onChange={e => setEditForm({ ...editForm, linked_driver_id: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                      <option value="0">— None —</option>
                      {drivers.map(d => (
                        <option key={d.id} value={d.id}>{d.name}{d.card_number ? ` (${d.card_number})` : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button onClick={() => setEditMode(false)} className="px-4 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                  <button onClick={saveEdit} disabled={saving} className="px-4 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded border border-gray-200">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">User Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 px-5 py-4">
                  <div>
                    <span className="text-xs text-gray-400">Full Name</span>
                    <p className="text-sm text-gray-900 font-medium">{selected.first_name} {selected.last_name}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Email</span>
                    <p className="text-sm text-gray-900">{selected.email}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Phone</span>
                    <p className="text-sm text-gray-900">{selected.phone || '—'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Role</span>
                    <p className="text-sm text-gray-900">{ROLE_LABELS[selected.role] || selected.role}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Company</span>
                    <p className="text-sm text-gray-900">{selected.company_name || '—'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Status</span>
                    <p className="text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[selected.status]}`}>
                        {selected.status.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Account Created</span>
                    <p className="text-sm text-gray-900">{formatDate(selected.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Last Login</span>
                    <p className="text-sm text-gray-900">{formatDateTime(selected.last_login)}</p>
                    {selected.last_login && <p className="text-xs text-gray-400">{timeSince(selected.last_login)}</p>}
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Linked Driver / Tacho Card</span>
                    <p className="text-sm text-gray-900">{selected.linked_driver_name || '— Not linked —'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Module Access ────────────────────────────────────── */}
            <div className="bg-white rounded border border-gray-200">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Module Access</h3>
                {isManager && selected.id !== currentUser?.id && !editModules && (
                  <button onClick={() => startModuleEdit(selected)} className="text-xs text-blue-600 hover:text-blue-800">Configure</button>
                )}
              </div>

              {editModules ? (
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs text-gray-500">Select which pages this user can access.</p>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedModules(ALL_MODULES.map(m => m.key))} className="text-xs text-blue-600 hover:text-blue-800">Select All</button>
                      <button onClick={() => setSelectedModules([])} className="text-xs text-gray-500 hover:text-gray-700">Clear All</button>
                    </div>
                  </div>

                  {['Main', 'Compliance', 'Operations', 'Commercial', 'Administration'].map(section => {
                    const mods = ALL_MODULES.filter(m => m.section === section);
                    return (
                      <div key={section} className="mb-4">
                        <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">{section}</h4>
                        <div className="grid grid-cols-3 gap-2">
                          {mods.map(m => (
                            <label key={m.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer px-2 py-1.5 rounded hover:bg-gray-50">
                              <input type="checkbox" checked={selectedModules.includes(m.key)}
                                onChange={e => {
                                  if (e.target.checked) setSelectedModules([...selectedModules, m.key]);
                                  else setSelectedModules(selectedModules.filter(k => k !== m.key));
                                }}
                                className="rounded border-gray-300" />
                              {m.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                    <button onClick={() => setEditModules(false)} className="px-4 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={saveModules} disabled={saving} className="px-4 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                      {saving ? 'Saving...' : 'Save Permissions'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-5">
                  {!selected.allowed_modules ? (
                    <p className="text-sm text-green-700">Full access — all modules enabled</p>
                  ) : selected.allowed_modules.length === 0 ? (
                    <p className="text-sm text-red-600">No module access configured</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_MODULES.map(m => {
                        const enabled = selected.allowed_modules!.includes(m.key);
                        return (
                          <span key={m.key}
                            className={`text-xs px-2 py-0.5 rounded ${enabled ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-300 line-through'}`}>
                            {m.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Activity Log (future placeholder) ────────────────── */}
            <div className="bg-white rounded border border-gray-200">
              <div className="px-5 py-3 border-b border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Activity</h3>
              </div>
              <div className="px-5 py-4 space-y-2">
                {selected.created_at && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-gray-500">{formatDateTime(selected.created_at)}</span>
                    <span className="text-gray-700">Account created</span>
                  </div>
                )}
                {selected.last_login && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-gray-500">{formatDateTime(selected.last_login)}</span>
                    <span className="text-gray-700">Last sign-in</span>
                  </div>
                )}
                {selected.status === 'suspended' && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="w-2 h-2 rounded-full bg-red-400" />
                    <span className="text-gray-700">Account suspended</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  /* ── Main users list view ──────────────────────────────────────────── */
  return (
    <PortalLayout currentPage="users">
      <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50">

        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-800">User Management</h1>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400">{users.length} users</span>
                {pendingCount > 0 && (
                  <>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-amber-600 font-medium">{pendingCount} pending</span>
                  </>
                )}
                <span className="text-gray-300 mx-1">|</span>
                <span className="text-green-600">{activeCount} active</span>
                {suspendedCount > 0 && (
                  <>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-red-500">{suspendedCount} suspended</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="text" placeholder="Search name, email, company..."
                value={search} onChange={e => setSearch(e.target.value)}
                className="w-56 px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-slate-400" />
              <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white">
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="transport_manager">Transport Manager</option>
                <option value="driver">Driver</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white">
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
              {isManager && (
                <button onClick={() => { setShowCreate(true); setError(''); }}
                  className="px-4 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">
                  + Create User
                </button>
              )}
            </div>
          </div>
        </div>

        {error && <div className="mx-6 mt-3 bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
        {success && <div className="mx-6 mt-3 bg-green-50 text-green-700 px-4 py-2 rounded text-sm">{success}</div>}

        {/* Pending approvals banner */}
        {pendingCount > 0 && filterStatus !== 'suspended' && (
          <div className="mx-6 mt-3 bg-amber-50 border border-amber-200 rounded p-3">
            <h3 className="text-xs font-medium text-amber-800 mb-2">Pending Approval ({pendingCount})</h3>
            <div className="space-y-1.5">
              {users.filter(u => u.status === 'pending').map(u => (
                <div key={u.id} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-700">
                      {u.first_name[0]}{u.last_name[0]}
                    </div>
                    <span className="text-sm text-gray-900">{u.first_name} {u.last_name}</span>
                    <span className="text-xs text-gray-400">{u.email}</span>
                  </div>
                  {isManager && (
                    <div className="flex gap-2">
                      <button onClick={() => handleStatusChange(u, 'active')} className="text-xs text-green-600 hover:text-green-800 font-medium">Approve</button>
                      <button onClick={() => handleStatusChange(u, 'suspended')} className="text-xs text-red-500 hover:text-red-700 font-medium">Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users table */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-16">Loading users...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-16">
              {users.length === 0 ? 'No users found.' : 'No users match your filters.'}
            </div>
          ) : (
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Linked Driver</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Access</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Last Login</th>
                    {isManager && <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase w-36">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(u)}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                            {u.first_name[0]}{u.last_name[0]}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{u.first_name} {u.last_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-700">{ROLE_LABELS[u.role] || u.role}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[u.status] || 'bg-gray-100 text-gray-600'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">
                        {u.linked_driver_name || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {!u.allowed_modules ? (
                          <span className="text-green-600">Full access</span>
                        ) : (
                          <span className="text-gray-500">{u.allowed_modules.length}/{ALL_MODULES.length} modules</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-xs text-gray-600">{formatDateTime(u.last_login)}</div>
                        {u.last_login && <div className="text-[10px] text-gray-400">{timeSince(u.last_login)}</div>}
                      </td>
                      {isManager && (
                        <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                          {u.id !== currentUser?.id && (
                            <div className="flex justify-end gap-2">
                              {u.status === 'pending' && (
                                <button onClick={() => handleStatusChange(u, 'active')} className="text-xs text-green-600 hover:text-green-800">Approve</button>
                              )}
                              {u.status === 'active' && (
                                <button onClick={() => handleSuspend(u)} className="text-xs text-amber-600 hover:text-amber-800">Suspend</button>
                              )}
                              {u.status === 'suspended' && (
                                <button onClick={() => handleActivate(u)} className="text-xs text-green-600 hover:text-green-800">Activate</button>
                              )}
                              <button onClick={() => handleResetPassword(u)} className="text-xs text-blue-600 hover:text-blue-800">Reset PW</button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create User Modal ──────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-lg mx-4 p-5">
            <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-4">Create New User</h2>
            {error && <div className="bg-red-50 text-red-700 px-3 py-2 rounded mb-3 text-sm">{error}</div>}

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">First Name *</label>
                  <input type="text" value={createForm.first_name} onChange={e => setCreateForm({ ...createForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Last Name *</label>
                  <input type="text" value={createForm.last_name} onChange={e => setCreateForm({ ...createForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email *</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Temporary Password *</label>
                <input type="text" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded" placeholder="Set an initial password" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Role</label>
                  <select value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-white">
                    <option value="driver">Driver</option>
                    <option value="transport_manager">Transport Manager</option>
                    {isAdmin && <option value="admin">Administrator</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Phone</label>
                  <input type="tel" value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={handleCreate} disabled={saving}
                className="px-6 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50">
                {saving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
